import { useEffect, useState } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import { DownloadSimple, Flask, FileText, Lock, Clock, PlayCircle, X } from "@phosphor-icons/react";

export default function RecipeVault() {
    const [recipes, setRecipes] = useState([]);
    const [downloadingId, setDownloadingId] = useState(null);
    const [walkthroughUrl, setWalkthroughUrl] = useState(null); // currently-open walkthrough video

    useEffect(() => {
        api.get("/recipes").then((r) => setRecipes(r.data)).catch(() => {});
    }, []);

    const downloadPdf = async (recipe) => {
        if (!recipe.pdf_file_id) return;
        setDownloadingId(recipe.id);
        try {
            const r = await api.get(`/files/${recipe.pdf_file_id}/download`, {
                responseType: "blob",
            });
            const url = URL.createObjectURL(new Blob([r.data], { type: "application/pdf" }));
            const a = document.createElement("a");
            a.href = url;
            const safe = recipe.title.replace(/[^a-z0-9]+/gi, "-").toLowerCase();
            a.download = `${safe}.pdf`;
            document.body.appendChild(a);
            a.click();
            a.remove();
            URL.revokeObjectURL(url);
        } catch (err) {
            toast.error("Download failed");
        } finally {
            setDownloadingId(null);
        }
    };

    return (
        <section data-testid="recipe-vault-section" className="bg-[#0A0A0A] border border-white/10 relative overflow-hidden">
            <div className="px-6 py-4 border-b border-white/10 flex items-center justify-between">
                <div className="flex items-center gap-3">
                    <Flask size={22} className="text-electric-blue" weight="duotone" />
                    <div>
                        <p className="label-mono text-electric-blue">THE RECIPE VAULT</p>
                        <h2 className="font-heading font-bold text-lg">Viral Blueprints</h2>
                    </div>
                </div>
                <span className="label-mono text-[10px]">{recipes.length} BLUEPRINTS UNLOCKED</span>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 p-6">
                {recipes.map((r, i) => {
                    const isComingSoon = r.status === "coming_soon" || !r.pdf_file_id;
                    return (
                        <div
                            key={r.id}
                            data-testid={`recipe-card-${i}`}
                            className={`bg-[#060606] border border-white/10 transition-all group flex flex-col ${
                                isComingSoon
                                    ? "opacity-90"
                                    : "hover:border-electric-blue/60 hover:-translate-y-1"
                            }`}
                        >
                            <div className="aspect-[4/3] relative overflow-hidden">
                                <img
                                    src={r.cover_url}
                                    alt={r.title}
                                    className={`w-full h-full object-cover transition-transform duration-500 ${
                                        isComingSoon ? "grayscale brightness-50" : "group-hover:scale-105"
                                    }`}
                                />
                                <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent" />
                                <div className="absolute top-3 left-3 px-2 py-1 bg-black/70 border border-electric-blue/50">
                                    <span className="label-mono text-[9px] text-electric-blue">{r.category}</span>
                                </div>
                                {!isComingSoon && r.rpm_estimate && (
                                    <div className="absolute bottom-3 right-3 px-2 py-1 bg-electric-blue text-white">
                                        <span className="label-mono text-[10px]">RPM {r.rpm_estimate}</span>
                                    </div>
                                )}
                                {isComingSoon && (
                                    <div className="absolute inset-0 flex items-center justify-center">
                                        <div className="px-3 py-2 bg-black/85 border border-yellow-500/50 flex items-center gap-2">
                                            <Clock size={14} weight="fill" className="text-yellow-400" />
                                            <span className="label-mono text-[10px] text-yellow-400">
                                                {r.release_label || "COMING SOON"}
                                            </span>
                                        </div>
                                    </div>
                                )}
                            </div>
                            <div className="p-4 flex-1 flex flex-col">
                                <h3
                                    className={`font-heading font-bold text-base mb-2 line-clamp-2 ${
                                        isComingSoon ? "text-muted-foreground" : "group-hover:text-electric-blue transition-colors"
                                    }`}
                                >
                                    {r.title}
                                </h3>
                                <p className="text-xs text-muted-foreground mb-4 leading-relaxed line-clamp-3 flex-1">
                                    {r.description}
                                </p>
                                {isComingSoon ? (
                                    <div
                                        data-testid={`recipe-locked-${i}`}
                                        className="inline-flex items-center justify-center gap-2 bg-yellow-500/5 border border-yellow-500/30 text-yellow-400 px-3 py-2 text-xs label-mono"
                                    >
                                        <Lock size={12} weight="fill" />
                                        FOUNDER EARLY ACCESS
                                    </div>
                                ) : (
                                    <div className="flex flex-col gap-2">
                                        <button
                                            onClick={() => downloadPdf(r)}
                                            disabled={downloadingId === r.id}
                                            data-testid={`recipe-download-${i}`}
                                            className="inline-flex items-center justify-center gap-2 bg-transparent border border-electric-blue text-electric-blue hover:bg-electric-blue hover:text-white px-3 py-2 text-xs label-mono transition-all disabled:opacity-50"
                                        >
                                            <DownloadSimple size={14} weight="bold" />
                                            {downloadingId === r.id ? "DOWNLOADING..." : "DOWNLOAD PDF"}
                                        </button>
                                        {r.walkthrough_video_url && (
                                            <button
                                                onClick={() => setWalkthroughUrl(r.walkthrough_video_url)}
                                                data-testid={`recipe-walkthrough-${i}`}
                                                className="inline-flex items-center justify-center gap-2 bg-electric-blue/10 border border-electric-blue/40 text-electric-blue hover:bg-electric-blue hover:text-white px-3 py-2 text-xs label-mono transition-all"
                                            >
                                                <PlayCircle size={14} weight="fill" />
                                                HOW TO BUILD FROM THIS
                                            </button>
                                        )}
                                    </div>
                                )}
                            </div>
                        </div>
                    );
                })}
                {recipes.length === 0 && (
                    <div className="col-span-full p-8 text-center">
                        <FileText size={32} className="text-muted-foreground mx-auto mb-3" />
                        <p className="label-mono text-muted-foreground">VAULT INITIALIZING...</p>
                    </div>
                )}
            </div>

            {walkthroughUrl && (
                <WalkthroughModal url={walkthroughUrl} onClose={() => setWalkthroughUrl(null)} />
            )}
        </section>
    );
}

function WalkthroughModal({ url, onClose }) {
    const isMp4 = /\.(mp4|webm|mov)(\?|$)/i.test(url);
    return (
        <div
            data-testid="walkthrough-modal"
            className="fixed inset-0 z-50 flex items-center justify-center px-4 py-8 bg-black/90 backdrop-blur-sm"
            onClick={onClose}
        >
            <div
                className="relative w-full max-w-4xl bg-[#0A0A0A] border border-electric-blue neon-border"
                onClick={(e) => e.stopPropagation()}
            >
                <button
                    onClick={onClose}
                    data-testid="walkthrough-close"
                    aria-label="Close walkthrough"
                    className="absolute -top-3 -right-3 z-10 w-9 h-9 bg-electric-blue text-white flex items-center justify-center hover:bg-electric-blue-dark transition-colors"
                >
                    <X size={16} weight="bold" />
                </button>
                <div className="aspect-video bg-black">
                    {isMp4 ? (
                        <video src={url} controls autoPlay playsInline className="w-full h-full object-cover" />
                    ) : (
                        <iframe
                            src={url}
                            title="Recipe Walkthrough"
                            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                            allowFullScreen
                            className="w-full h-full"
                        />
                    )}
                </div>
            </div>
        </div>
    );
}
